import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
import { FormGroup, Label } from 'reactstrap';

import { MediaImage, resizeImage } from '@techchange/filestack';
import { Button } from '@techchange/components';
import { messages } from '../../i18n';

import TemplateEditor from './TemplateEditor';

import styles from './CertificateEditor.module.scss';

const IMAGE_PREVIEW_HEIGHT = 100;

const propTypes = {

	/**
	 * The initial field values for the template if it exists.
	 */
	initialValues: PropTypes.shape({

		/**
		 * The UUID for the certificate.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The UUID of the associated certificate layout
		 */
		certificate_layout_id: PropTypes.string.isRequired,

		/**
		 * The associated certificate layout.
		 */
		certificate_layout: PropTypes.shape({

			/**
			 * The UUID for the certificate layout.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The layout HTML.
			 */
			html_body: PropTypes.string.isRequired,

		}).isRequired,

		/**
		 * The associated templates.
		 */
		certificate_templates: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The certificate template content, represented as a ProseMirror object.
			 */
			content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

			/**
			 * The language of the template.
			 */
			language: PropTypes.string.isRequired,

			/**
			 * The list of certificate people associated with the template.
			 */
			certificate_people: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The certificate person's ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * The certificate person's weight.
				 */
				weight: PropTypes.number.isRequired,

				/**
				 * The ID of the associated certificate template.
				 */
				certificate_template_id: PropTypes.string.isRequired,

				/**
				 * The ID of the associated person.
				 */
				person_id: PropTypes.string.isRequired,

			})),

		})).isRequired,

		/**
		 * Image source for the logo.
		 */
		logo: PropTypes.string,

		/**
		 * Image source for the badge.
		 */
		badge: PropTypes.string,

		/**
		 * Image source for the QR code.
		 */
		qr_code: PropTypes.string,

		/**
		 * Image source for the template background image.
		 */
		background_image: PropTypes.string,

	}),

	/**
	 * Flag indicating if styling for wider screens should be applied.
	 */
	widescreen: PropTypes.bool,

	/**
	 * The list of all available people for this organization.
	 */
	people: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	})).isRequired,

	/**
	 * The organization object.
	 */
	organization: PropTypes.shape({

		id: PropTypes.string.isRequired,

		emails: PropTypes.shape({

			primary_color: PropTypes.string,

		}),

	}),

	/**
	 * The filestack credentials object.
	 */
	filestack: PropTypes.shape({

		apiKey: PropTypes.string.isRequired,

		security: PropTypes.shape({

			policy: PropTypes.string.isRequired,

			signature: PropTypes.string.isRequired,

		}).isRequired,

	}).isRequired,

	/**
	 * Callback function to handle changes to form inputs.
	 */
	onChange: PropTypes.func.isRequired,

};

const defaultProps = {
	initialValues: {},
};

const CertificateEditor = (props) => {

	const { initialValues, widescreen, people, organization, filestack, onChange } = props;

	const [formData, setFormData] = useState({
		logo: initialValues.logo,
		badge: initialValues.badge,
		qr_code: initialValues.qr_code,
		background_image: initialValues.background_image,
		certificate_templates: initialValues.certificate_templates,
		certificate_layout_id: initialValues.certificate_layout_id,
	});

	const [peopleData, setPeopleData] = useState(people);

	const handleOnInputChange = (fieldName, fieldValue) => {

		const updatedState = {
			[fieldName]: fieldValue,
		};

		setFormData(Object.assign({}, formData, updatedState));

	};

	const handleOnPersonChange = (newPerson) => {

		setPeopleData((prevState) => {

			const result = [...prevState];

			const currentPersonIdx = prevState.findIndex((person) => person.id === newPerson.id);

			if (currentPersonIdx >= 0) {

				result[currentPersonIdx] = newPerson;

			} else {

				result.push(newPerson);

			}

			return result;

		});

	};

	useEffect(() => {

		onChange(formData);

	}, [formData, onChange]);

	// Render certificate preview and form inputs
	const { logo, badge, qr_code: qrCode, background_image: backgroundImage } = formData;

	const intl = useIntl();

	return (
		<div className={styles['certificate-template-form']}>
			{/* TODO: Use Slider component here to toggle between multiple templates */}
			{formData.certificate_templates && formData.certificate_templates.length > 0 &&
				<TemplateEditor
					index={0}
					formData={formData}
					widescreen={widescreen}
					layout={initialValues.certificate_layout}
					people={peopleData}
					organization={organization}
					filestack={filestack}
					onPersonChange={handleOnPersonChange}
					onChange={handleOnInputChange} />
			}
			<h4 className="mt-3">
				<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_HEADER_ADDITIONAL_SETTINGS} />
			</h4>
			<hr />
			<div className="certificate-settings pt-4">
				<FormGroup className="mb-5">
					<Label for="logo">
						<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_FIELD_LABEL_LOGO} />
					</Label>
					<div>
						{logo &&
							<div className="img-preview my-2">
								<img
									alt={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_PREVIEW_LABEL)}
									src={resizeImage(logo, 'height', IMAGE_PREVIEW_HEIGHT)} />
							</div>
						}
						<div className="d-flex my-2">
							<MediaImage
								filestack={filestack}
								onSuccess={(url) => handleOnInputChange('logo', url)}>
								<Button
									color="neutral"
									outline>
									{logo
										? <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_CHANGE} />
										: <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_UPLOAD} />
									}
								</Button>
							</MediaImage>
							{logo &&
								<div className="ms-2">
									<Button
										color="outline-danger"
										onClick={() => handleOnInputChange('logo', null)}>
										<FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_REMOVE} />
									</Button>
								</div>
							}
						</div>
					</div>
				</FormGroup>
				<FormGroup className="mb-5">
					<Label for="badge">
						<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_FIELD_LABEL_BADGE} />
					</Label>
					<div>
						{badge &&
							<div className="img-preview my-2">
								<img
									alt={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_PREVIEW_LABEL)}
									src={resizeImage(badge, 'height', IMAGE_PREVIEW_HEIGHT)} />
							</div>
						}
						<div className="d-flex my-2">
							<MediaImage
								filestack={filestack}
								onSuccess={(url) => handleOnInputChange('badge', url)}>
								<Button
									color="neutral"
									outline>
									{badge
										? <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_CHANGE} />
										: <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_UPLOAD} />
									}
								</Button>
							</MediaImage>
							{badge &&
								<div className="ms-2">
									<Button
										color="outline-danger"
										onClick={() => handleOnInputChange('badge', null)}>
										<FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_REMOVE} />
									</Button>
								</div>
							}
						</div>
					</div>
				</FormGroup>
				<FormGroup className="mb-5">
					<Label for="qr_code">
						<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_FIELD_LABEL_QR_CODE} />
					</Label>
					<div>
						{qrCode &&
							<div className="img-preview my-2">
								<img
									alt={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_PREVIEW_LABEL)}
									src={resizeImage(qrCode, 'height', IMAGE_PREVIEW_HEIGHT)} />
							</div>
						}
						<div className="d-flex my-2">
							<MediaImage
								filestack={filestack}
								onSuccess={(url) => handleOnInputChange('qr_code', url)}>
								<Button
									color="neutral"
									outline>
									{qrCode
										? <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_CHANGE} />
										: <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_UPLOAD} />
									}
								</Button>
							</MediaImage>
							{qrCode &&
								<div className="ms-2">
									<Button
										color="outline-danger"
										onClick={() => handleOnInputChange('qr_code', null)}>
										<FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_REMOVE} />
									</Button>
								</div>
							}
						</div>
					</div>
				</FormGroup>
				<FormGroup className="mb-5">
					<Label for="background_image">
						<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_FIELD_LABEL_BACKGROUND_IMAGE} />
					</Label>
					<div>
						{backgroundImage &&
							<div className="img-preview my-2">
								<img
									alt={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_PREVIEW_LABEL)}
									src={resizeImage(backgroundImage, 'height', IMAGE_PREVIEW_HEIGHT)} />
							</div>
						}
						<div className="d-flex my-2">
							<MediaImage
								filestack={filestack}
								onSuccess={(url) => handleOnInputChange('background_image', url)}>
								<Button
									color="neutral"
									outline>
									{backgroundImage
										? <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_CHANGE} />
										: <FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_UPLOAD} />
									}
								</Button>
							</MediaImage>
							{backgroundImage &&
								<div className="ms-2">
									<Button
										color="outline-danger"
										onClick={() => handleOnInputChange('background_image', null)}>
										<FormattedMessage {...messages.TC_COMPONENTS_IMAGE_PICKER_BUTTON_TEXT_REMOVE} />
									</Button>
								</div>
							}
						</div>
					</div>
				</FormGroup>
			</div>
		</div>
	);

};

CertificateEditor.propTypes = propTypes;
CertificateEditor.defaultProps = defaultProps;

export default CertificateEditor;
